import { motion } from "motion/react";
import { Webhook, Zap, Send, Mail, Database, ShoppingCart, User, CheckCircle } from "lucide-react";
import { useState, useEffect } from "react";

export function WebhooksSection() {
  const [activeWebhook, setActiveWebhook] = useState(0);
  const [eventCount, setEventCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWebhook((prev) => (prev + 1) % 4);
      setEventCount((prev) => prev + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const webhookTriggers = [
    {
      icon: User,
      trigger: "new_visitor",
      title: "Novo Visitante Detectado",
      description: "Dispara quando um novo visitante único é identificado no site",
      example: "Criar registro no CRM + Iniciar sequência de nutrição",
      color: "#00F5FF"
    },
    {
      icon: ShoppingCart,
      trigger: "cart_abandoned",
      title: "Carrinho Abandonado",
      description: "Ativa quando um lead identificado abandona o checkout",
      example: "Email automático + Cupom de desconto + Notificação no Slack",
      color: "#FF4D00"
    },
    {
      icon: CheckCircle,
      trigger: "order_completed",
      title: "Compra Concluída",
      description: "Dispara imediatamente após confirmação de pedido",
      example: "Atualizar CRM + Trigger de upsell + Analytics",
      color: "#10B981"
    },
    {
      icon: Mail,
      trigger: "lead_captured",
      title: "Email Capturado",
      description: "Ativa quando um email é identificado (formulário, checkout, etc)",
      example: "Adicionar à lista + Welcome email + Score calculation",
      color: "#00F5FF"
    }
  ];

  const integrations = [
    { name: "Zapier", logo: "Z", color: "#FF4A00" },
    { name: "Make", logo: "M", color: "#6B46FF" },
    { name: "n8n", logo: "n8", color: "#EA4B71" },
    { name: "ActiveCampaign", logo: "AC", color: "#356AE6" },
    { name: "RD Station", logo: "RD", color: "#1BD6B1" },
    { name: "Slack", logo: "S", color: "#4A154B" }
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-[#0B0E14]/98 to-[#0B0E14]" />

      {/* Circuit board pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="circuit" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M0 50 L25 50 L25 25 L75 25 L75 75 L100 75" stroke="#00F5FF" strokeWidth="1" fill="none" />
              <circle cx="25" cy="50" r="3" fill="#00F5FF" />
              <circle cx="25" cy="25" r="3" fill="#00F5FF" />
              <circle cx="75" cy="25" r="3" fill="#00F5FF" />
              <circle cx="75" cy="75" r="3" fill="#00F5FF" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00F5FF]/30 bg-[#00F5FF]/5 mb-6">
            <Webhook className="w-4 h-4 text-[#00F5FF]" />
            <span className="text-sm text-[#00F5FF] font-mono">REAL-TIME WEBHOOKS</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Automação que Dispara
            <span className="block text-[#00F5FF]">No Momento Certo</span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Webhooks em tempo real conectam WPTT com todo seu stack de marketing. 
            Cada evento vira <span className="text-[#10B981] font-bold">ação automática instantânea</span>
          </p>
        </motion.div>

        {/* Main Visual Flow */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            {/* Left: Triggers */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="text-center lg:text-left mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Triggers</h3>
                <p className="text-sm text-gray-400">12 eventos rastreados</p>
              </div>

              {webhookTriggers.map((trigger, idx) => {
                const Icon = trigger.icon;
                const isActive = idx === activeWebhook;
                return (
                  <motion.div
                    key={idx}
                    whileHover={{ scale: 1.02, x: 5 }}
                    animate={{
                      borderColor: isActive ? trigger.color : `${trigger.color}30`,
                      backgroundColor: isActive ? `${trigger.color}10` : 'transparent'
                    }}
                    className="p-4 rounded-lg border cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center transition-all"
                        style={{
                          backgroundColor: isActive ? `${trigger.color}20` : `${trigger.color}10`,
                          border: `2px solid ${isActive ? trigger.color : trigger.color + '40'}`
                        }}
                      >
                        <Icon className="w-5 h-5" style={{ color: trigger.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-mono text-gray-400">{trigger.trigger}</div>
                        <div className="text-white font-semibold">{trigger.title}</div>
                      </div>
                      {isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"
                        />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Center: Flow Visualization */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {/* WPTT Core */}
              <div className="relative z-10">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="relative"
                >
                  <div className="absolute -inset-4 rounded-2xl bg-[#00F5FF] opacity-20 blur-2xl" />
                  <div className="relative w-48 h-48 mx-auto rounded-2xl border-2 border-[#00F5FF] bg-gradient-to-br from-[#00F5FF]/20 to-[#0B0E14] backdrop-blur-sm flex items-center justify-center">
                    <div className="text-center">
                      <Webhook className="w-12 h-12 text-[#00F5FF] mx-auto mb-2" />
                      <div className="text-white font-bold text-lg">WPTT Core</div>
                      <div className="text-[#00F5FF] text-sm font-mono">Webhook Engine</div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Animated connection lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                {/* Left line */}
                <motion.line
                  x1="0%"
                  y1="50%"
                  x2="30%"
                  y2="50%"
                  stroke="#00F5FF"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                />
                {/* Right line */}
                <motion.line
                  x1="70%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                  stroke="#00F5FF"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear', delay: 0.5 }}
                />
              </svg>

              {/* Event counter */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 backdrop-blur-sm"
              >
                <div className="text-center">
                  <div className="text-[#10B981] font-mono text-sm">
                    {eventCount.toString().padStart(4, '0')} eventos/hoje
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Integrations */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="text-center lg:text-left mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Integrações</h3>
                <p className="text-sm text-gray-400">Conecte com qualquer ferramenta</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {integrations.map((integration, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="p-4 rounded-lg border border-gray-700 bg-gradient-to-br from-[#0B0E14] to-[#0B0E14]/50 backdrop-blur-sm text-center cursor-pointer hover:border-[#00F5FF]/50 transition-all"
                  >
                    <div
                      className="w-12 h-12 rounded-lg mx-auto mb-2 flex items-center justify-center font-bold text-white"
                      style={{ backgroundColor: integration.color }}
                    >
                      {integration.logo}
                    </div>
                    <div className="text-sm text-gray-300">{integration.name}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Trigger Details */}
        <motion.div
          key={activeWebhook}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          <div
            className="p-8 rounded-xl border backdrop-blur-sm"
            style={{
              borderColor: `${webhookTriggers[activeWebhook].color}30`,
              background: `linear-gradient(135deg, ${webhookTriggers[activeWebhook].color}10 0%, transparent 100%)`
            }}
          >
            <div className="flex items-start gap-6">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: `${webhookTriggers[activeWebhook].color}20`,
                  border: `2px solid ${webhookTriggers[activeWebhook].color}`
                }}
              >
                {(() => {
                  const Icon = webhookTriggers[activeWebhook].icon;
                  return <Icon className="w-8 h-8" style={{ color: webhookTriggers[activeWebhook].color }} />;
                })()}
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-2xl font-bold text-white">
                    {webhookTriggers[activeWebhook].title}
                  </h3>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-mono"
                    style={{
                      backgroundColor: `${webhookTriggers[activeWebhook].color}20`,
                      color: webhookTriggers[activeWebhook].color,
                      border: `1px solid ${webhookTriggers[activeWebhook].color}40`
                    }}
                  >
                    {webhookTriggers[activeWebhook].trigger}
                  </span>
                </div>

                <p className="text-gray-300 mb-4">
                  {webhookTriggers[activeWebhook].description}
                </p>

                <div className="p-4 rounded-lg bg-[#0B0E14]/50 border border-gray-700">
                  <div className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Exemplo de automação:</div>
                      <div className="text-white font-mono text-sm">
                        {webhookTriggers[activeWebhook].example}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Code Example */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-4xl mx-auto mt-12"
        >
          <div className="p-8 rounded-xl border border-[#00F5FF]/30 bg-gradient-to-br from-[#0B0E14] to-[#0B0E14]/50 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-[#00F5FF]" />
              <span className="text-[#00F5FF] font-mono text-sm">PAYLOAD EXAMPLE</span>
            </div>
            <pre className="text-sm text-gray-300 font-mono overflow-x-auto">
{`{
  "event": "cart_abandoned",
  "timestamp": "2026-01-17T14:35:42Z",
  "user": {
    "id": "lead_abc123",
    "email": "joao.silva@email.com",
    "name": "João Silva"
  },
  "cart": {
    "total": 2847.00,
    "items": 3,
    "products": ["iPhone 15 Pro", "AirPods Pro"]
  },
  "context": {
    "utm_source": "google",
    "utm_campaign": "black_friday",
    "last_page": "/checkout"
  }
}`}
            </pre>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-xl text-gray-300 mb-8">
            Configure uma vez. <span className="text-[#00F5FF] font-bold">Automatize para sempre.</span>
          </p>
          <button className="px-10 py-5 bg-[#FF4D00] hover:bg-[#FF4D00]/90 text-white text-lg rounded-lg font-bold transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#FF4D00]/50">
            Ativar Webhooks Agora
          </button>
        </motion.div>
      </div>
    </section>
  );
}
